import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Download, Upload, Check, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicTemplateConsent() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => {
    axios.get(`${API}/public/consent-template-request/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || t("templateConsent.invalidLink")));
  };
  useEffect(load, [token]);

  async function upload(e) {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await axios.post(`${API}/public/consent-template-request/${token}/upload-signed`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("templateConsent.uploaded")); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("common.error")); }
    finally { setUploading(false); e.target.value = ""; }
  }

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-6 text-center relative">
      <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
      <div><p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-3">{t("common.error")}</p>
      <h2 className="font-heading text-3xl font-light">{err}</h2></div>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] text-stone-500 relative">
      <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
      {t("common.loading")}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAF9F6] py-10 px-4">
      <Toaster position="top-center" richColors />
      <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
      <div className="max-w-2xl mx-auto fade-in-up space-y-6">
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-2">{t("templateConsent.eyebrow")}</p>
          <h1 className="font-heading text-4xl md:text-5xl font-light">{data.treatment || t("templateConsent.consent")}</h1>
          {data.patient_name && <p className="text-stone-500 mt-3">{t("publicConsent.patient")} <b>{data.patient_name}</b></p>}
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8 space-y-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#D4A373]/20 flex items-center justify-center">
              <span className="font-heading text-xl text-[#B07D4F]">1</span>
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-2xl font-light">{t("templateConsent.step1")}</h3>
              <p className="text-sm text-stone-600 mt-1">{t("templateConsent.step1Desc")}</p>
              <a href={`${API}/public/consent-template-request/${token}/download`} target="_blank" rel="noreferrer"
                data-testid="tpl-download-pdf"
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#B07D4F] hover:text-white transition-colors">
                <Download className="w-4 h-4" /> {t("templateConsent.downloadPdf")}
                <span className="text-xs opacity-70">({data.template_filename})</span>
              </a>
            </div>
          </div>

          <div className="border-t border-stone-100 pt-5 flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#D4A373]/20 flex items-center justify-center">
              <span className="font-heading text-xl text-[#B07D4F]">2</span>
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-2xl font-light">{t("templateConsent.step2")}</h3>
              <p className="text-sm text-stone-600 mt-1">{t("templateConsent.step2Desc")}</p>
              <input ref={fileRef} type="file" accept="application/pdf,image/*" onChange={upload} className="hidden" data-testid="tpl-upload-input" />
              <button onClick={() => fileRef.current.click()} disabled={uploading} data-testid="tpl-upload-btn"
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50">
                <Upload className="w-4 h-4" />
                {uploading ? t("patientDetail.uploading") : data.signed_uploaded ? t("templateConsent.replace") : t("templateConsent.uploadSigned")}
              </button>
              {data.signed_uploaded && (
                <p className="text-xs text-[#8FA08B] mt-3 flex items-center gap-1"><Check className="w-3 h-3" /> {t("templateConsent.received")}</p>
              )}
            </div>
          </div>
        </div>

        <p className="text-xs text-stone-400 text-center">{t("templateConsent.footer")}</p>
      </div>
    </div>
  );
}
